import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../AuthProvider";
import { handleRedirectCallback } from "../../services/msal.service";
import { InlineSpinner } from "../../components/inline-spinner";
import { API_URL } from "../../services/service.base";
import { Roles } from "../../types/user.types";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuthContext();
  const [error, setError] = useState<string | null>(null);
  // Use ref instead of state to persist across StrictMode remounts
  const isProcessingRef = useRef(false);

  useEffect(() => {
    // Prevent double-execution from React StrictMode
    if (isProcessingRef.current) return;

    const handleCallback = async () => {
      isProcessingRef.current = true;

      try {
        const result = await handleRedirectCallback();

        if (!result || !result.idToken) {
          // No redirect result - might be a direct navigation to this page
          // Check if we're already authenticated (token in localStorage)
          if (localStorage.getItem("accessToken")) {
            return; // Already authenticated, don't redirect
          }
          navigate("/login");
          return;
        }

        // Get the expected provider from session storage for security validation
        const expectedProvider = sessionStorage.getItem("entraAuthProvider");

        // Send the Entra ID token to backend to validate and get user info
        const response = await fetch(`${API_URL}authenticate/entra`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            idToken: result.idToken,
            accessToken: result.accessToken,
            expectedProvider: expectedProvider,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Authentication failed");
        }

        const userDto = await response.json();

        // Clean up session storage after successful authentication
        sessionStorage.removeItem("entraAuthProvider");

        // Set authenticated user and navigate
        setAuthenticatedUser(userDto);

        if (userDto.roleId === Roles.Admin) {
          navigate("/admin");
        } else if (userDto.roleId === Roles.Manager) {
          navigate("/manager");
        } else if (userDto.roleId === Roles.Reviewer) {
          navigate("/work");
        } else {
          navigate("/");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Authentication failed");
      }
    };

    handleCallback();
  }, [navigate, setAuthenticatedUser]);

  if (error) {
    return (
      <div className="card card-container tablet-left">
        <h2>Authentication Error</h2>
        <div className="alert alert-danger mt-3">{error}</div>
        <button
          className="btn btn-primary mt-3"
          onClick={() => navigate("/login")}
        >
          Return to Login
        </button>
      </div>
    );
  }

  return (
    <div className="card card-container tablet-left">
      <h2>Signing In...</h2>
      <div className="d-flex justify-content-center mt-4">
        <InlineSpinner />
      </div>
      <p className="text-center mt-3">Please wait while we complete your sign-in.</p>
    </div>
  );
};

export default AuthCallback;
